import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import type {
    Project,
    ProjectDeleteDeviceListResponse,
    ProjectDeviceReassignRequest,
    ProjectDeviceReassignResponse,
    ProjectDeviceReassignmentSummary,
    ProjectLookupItem,
    ProjectOwnerCandidate,
    ViewerProjectScope,
} from '../types/project'

const PROJECT_DEVICE_REASSIGNMENT_TIMEOUT_MS = 60000

export interface CreateProjectRequest {
    name: string
    parent_id?: number | null
    owner_user_id?: number | null
    region_id?: number | null
}

export interface UpdateProjectRequest {
    name?: string
    owner_user_id?: number | null
    region_id?: number | null
}

export const listProjects = async (): Promise<Project[]> => {
    const response = await api.get<Project[]>('/projects')
    return response.data
}

export const lookupProjects = async (params?: {
    includeDisabled?: boolean
    forManagement?: boolean
}): Promise<ProjectLookupItem[]> => {
    const response = await api.get<ProjectLookupItem[]>('/projects/lookup', {
        params: {
            include_disabled: params?.includeDisabled,
            for_management: params?.forManagement,
        },
    })
    return response.data
}

export const createProject = async (
    payload: CreateProjectRequest,
    options?: MutationRequestOptions
): Promise<Project> => {
    const response = await api.post<Project>(
        '/projects',
        payload,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const updateProject = async (
    projectId: number,
    payload: UpdateProjectRequest,
    options?: MutationRequestOptions
): Promise<Project> => {
    const response = await api.patch<Project>(
        `/projects/${projectId}`,
        payload,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const updateProjectStatus = async (
    projectId: number,
    status: 'enabled' | 'disabled',
    options?: MutationRequestOptions
): Promise<Project> => {
    const response = await api.post<Project>(
        `/projects/${projectId}/status`,
        { status },
        getMutationRequestConfig(options)
    )
    return response.data
}

export const deleteProject = async (
    projectId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    const config = getMutationRequestConfig(options)
    await api.delete(`/projects/${projectId}`, config)
}

export const getProjectDeviceReassignmentSummary = async (
    projectId: number
): Promise<ProjectDeviceReassignmentSummary> => {
    const response = await api.get<ProjectDeviceReassignmentSummary>(
        `/projects/${projectId}/device-reassignment-summary`
    )
    return response.data
}

export const listProjectDeleteDevices = async (
    projectId: number,
    params: {
        page?: number
        page_size?: number
        search_name?: string
        source_project_id?: number | null
    }
): Promise<ProjectDeleteDeviceListResponse> => {
    const response = await api.get<ProjectDeleteDeviceListResponse>(
        `/projects/${projectId}/delete-devices`,
        {
            params: {
                page: params.page,
                page_size: params.page_size,
                search_name: params.search_name || undefined,
                source_project_id: params.source_project_id || undefined,
            },
        }
    )
    return response.data
}

export const reassignProjectDevices = async (
    projectId: number,
    payload: ProjectDeviceReassignRequest,
    options?: MutationRequestOptions
): Promise<ProjectDeviceReassignResponse> => {
    const config = getMutationRequestConfig(options)
    config.timeout = PROJECT_DEVICE_REASSIGNMENT_TIMEOUT_MS
    const response = await api.post<ProjectDeviceReassignResponse>(
        `/projects/${projectId}/reassign-devices`,
        payload,
        config
    )
    return response.data
}

export const listProjectOwnerCandidates = async (
    projectId: number
): Promise<ProjectOwnerCandidate[]> => {
    const response = await api.get<ProjectOwnerCandidate[]>(
        `/projects/${projectId}/owner-candidates`
    )
    return response.data
}

export const listRootProjectOwnerCandidates = async (): Promise<ProjectOwnerCandidate[]> => {
    const response = await api.get<ProjectOwnerCandidate[]>('/projects/owner-candidates')
    return response.data
}

export const listViewerScopes = async (
    projectId: number
): Promise<ViewerProjectScope[]> => {
    const response = await api.get<ViewerProjectScope[]>(
        `/projects/${projectId}/viewer-scopes`
    )
    return response.data
}

export const createViewerScope = async (
    projectId: number,
    viewerUserId: number,
    options?: MutationRequestOptions
): Promise<ViewerProjectScope> => {
    const response = await api.post<ViewerProjectScope>(
        `/projects/${projectId}/viewer-scopes`,
        { viewer_user_id: viewerUserId },
        getMutationRequestConfig(options)
    )
    return response.data
}

export const deleteViewerScope = async (
    projectId: number,
    viewerUserId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(
        `/projects/${projectId}/viewer-scopes/${viewerUserId}`,
        getMutationRequestConfig(options)
    )
}
